import React from 'react';
import { GridCellParams, GridColDef } from '@mui/x-data-grid';
import { IconButton, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PasswordIcon from '@mui/icons-material/Password';
import { TFunction } from 'i18next';
import StatusIndicator from '../components/StatusIndicator';
import { __String } from 'typescript';
import EditIcon from '@mui/icons-material/Edit';
import { useLastLoginRender } from '../hooks/useLastLoginRender';
import { FULLSTORY_MASK_CLASSNAME } from '../services/analyticsUtils';
class TableUtils {
  private getColumnDefinition(
    columns: GridColDef[],
    t: TFunction,
    onDelete:
      | ((
          evt: React.MouseEvent<HTMLButtonElement, MouseEvent>,
          params: GridCellParams,
        ) => void)
      | null,
    onPasswordReset: ((row: any) => void) | null,
    showCtt: boolean = true,
    deleteKey: string = 'common.delete',
    onEdit: ((row: any) => void) | null,
    stateColumnName: string,
  ): GridColDef[] {
    const deleteTitle = t(deleteKey);
    const editTitle =
      stateColumnName === 'vehicleState'
        ? t('vehicles.edit')
        : stateColumnName === 'driverState'
        ? t('drivers.edit')
        : t('common.edit');
    return [
      ...columns,
      {
        field: 'isClearToTransport',
        headerName: t('common.ctt') || '',
        width: 80,
        hide: !showCtt,
        renderCell: (params: GridCellParams) => (
          <StatusIndicator cell={params} />
        ),
      },
      {
        field: stateColumnName,
        headerName: t('common.active') || '',
        width: 80,
        renderCell: (params: GridCellParams) => (
          <StatusIndicator cell={params} />
        ),
      },
      {
        field: 'Actions',
        headerName: ' ',
        width: onPasswordReset ? 180 : onEdit ? 120 : 80,
        sortable: false,
        hide: !onDelete,
        renderCell: (params: GridCellParams) => (
          <>
            {(params.row.transportationProviderTierBC === 'TIER1' ||
              params.row.transportationProviderTierBC === 'TIER3' ||
              stateColumnName === 'vehicleState' ||
              stateColumnName === 'driverState') && (
              <Tooltip title={deleteTitle}>
                <IconButton
                  data-testid="delete"
                  onClick={(evt) => (onDelete ? onDelete(evt, params) : null)}
                  color="primary"
                  size="large">
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )}
            {onPasswordReset && (
              <Tooltip title={t('users.resetPassword')}>
                <IconButton
                  data-testid="password"
                  onClick={(evt) => onPasswordReset(params.row)}
                  color="default"
                  size="large">
                  <PasswordIcon />
                </IconButton>
              </Tooltip>
            )}
            {onEdit && (
              <Tooltip title={editTitle}>
                <IconButton
                  data-testid="edit"
                  onClick={(evt) => onEdit(params.row)}
                  size="large"
                  color="default">
                  <EditIcon />
                </IconButton>
              </Tooltip>
            )}
          </>
        ),
      },
    ];
  }

  public getProviderColumnDefinition(
    renderProviderName: (params: GridCellParams) => React.ReactElement,
    renderDispatchEmail: (params: GridCellParams) => React.ReactElement,
    renderDispatchPhone: (params: GridCellParams) => React.ReactElement,
    renderMainPhone: (params: GridCellParams) => React.ReactElement,
    renderCoverageArea: (params: GridCellParams) => React.ReactElement,
    t: TFunction,
    onDelete:
      | ((
          evt: React.MouseEvent<HTMLButtonElement, MouseEvent>,
          params: GridCellParams,
        ) => void)
      | null,
  ) {
    return this.getColumnDefinition(
      [
        {
          field: 'transportationProviderID',
          headerName: t('common.id') || '',
          hide: true,
        },
        {
          field: 'transportationProviderName',
          headerName: t('provider.name') || '',
          flex: 2,
          renderCell: renderProviderName,
        },
        {
          field: 'transportationProviderTierID',
          headerName: t('provider.tier') || '',
          width: 40,
        },
        {
          field: 'EmailAddress',
          headerName: t('providers.dispatchEmail') || '',
          flex: 1,
          sortable: false,
          renderCell: renderDispatchEmail,
          cellClassName: FULLSTORY_MASK_CLASSNAME,
        },
        {
          field: 'MainPhone',
          headerName: t('providers.mainPhone') || '',
          flex: 1,
          sortable: false,
          renderCell: renderMainPhone,
          cellClassName: FULLSTORY_MASK_CLASSNAME,
        },
        {
          field: 'DispatchPhone',
          headerName: t('providers.dispatchPhone') || '',
          flex: 1,
          sortable: false,
          renderCell: renderDispatchPhone,
          cellClassName: FULLSTORY_MASK_CLASSNAME,
        },
        {
          field: 'ServiceArea',
          headerName: t('providers.serviceArea') || '',
          flex: 2,
          sortable: false,
          renderCell: renderCoverageArea,
        },
      ],
      t,
      onDelete,
      null,
      true,
      '',
      null,
      'transportationProviderState',
    );
  }

  public getTier3ProviderColumnDefinition(
    renderProviderName: (params: GridCellParams) => React.ReactElement,
    renderCoverageArea: (params: GridCellParams) => React.ReactElement,
    t: TFunction,
    onDelete:
      | ((
          evt: React.MouseEvent<HTMLButtonElement, MouseEvent>,
          params: GridCellParams,
        ) => void)
      | null,
  ) {
    return [
      {
        field: 'transportationProviderID',
        headerName: t('common.id') || '',
        hide: true,
      },
      {
        field: 'name',
        headerName: t('provider.name') || '',
        flex: 2,
        renderCell: renderProviderName,
      },
      {
        field: 'city',
        headerName: t('providers.city') || '',
        flex: 1,
        sortable: false,
        // renderCell: renderDispatchEmail,
        // cellClassName: FULLSTORY_MASK_CLASSNAME,
      },
      {
        field: 'state',
        headerName: t('providers.state') || '',
        flex: 1,
        sortable: false,
        // renderCell: renderMainPhone,
        // cellClassName: FULLSTORY_MASK_CLASSNAME,
      },
      {
        field: 'coverageAreas',
        headerName: t('providers.serviceArea') || '',
        flex: 2,
        sortable: false,
        // renderCell: renderCoverageArea,
      },
      {
        field: 'isActive',
        headerName: t('common.active') || '',
        width: 80,
        renderCell: (params: GridCellParams) => (
          <StatusIndicator cell={params} />
        ),
      },
      {
        field: 'Actions',
        headerName: ' ',
        width: 80,
        sortable: false,
        hide: !onDelete,
        renderCell: (params: GridCellParams) => (
          <>
            <Tooltip title={t('common.delete')}>
              <IconButton
                data-testid="delete"
                onClick={(evt) => (onDelete ? onDelete(evt, params) : null)}
                color="primary"
                size="large">
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </>
        ),
      },
    ];
  }

  public getDriverColumnDefinition(
    renderDriverName: (params: GridCellParams) => React.ReactElement,
    renderDriverPhone: (params: GridCellParams) => React.ReactElement,
    renderDriverEmail: (params: GridCellParams) => React.ReactElement,
    renderUsername: (params: GridCellParams) => React.ReactElement,
    t: TFunction,
    onDelete:
      | ((
          evt: React.MouseEvent<HTMLButtonElement, MouseEvent>,
          params: GridCellParams,
        ) => void)
      | null,
    onPasswordReset: ((row: any) => void) | null,
    onDriverEdit: ((row: any) => void) | null,
  ) {
    return this.getColumnDefinition(
      [
        { field: 'driverId', headerName: 'ID', hide: true },
        {
          field: 'driverLastName',
          headerName: t('drivers.lastName') || '',
          flex: 1,
          renderCell: renderDriverName,
          cellClassName: FULLSTORY_MASK_CLASSNAME,
        },
        {
          field: 'driverFirstName',
          headerName: t('drivers.firstName') || '',
          flex: 1,
          cellClassName: FULLSTORY_MASK_CLASSNAME,
        },
        {
          field: 'driverPhone',
          headerName: t('common.phone') || '',
          flex: 1,
          sortable: false,
          renderCell: renderDriverPhone,
          cellClassName: FULLSTORY_MASK_CLASSNAME,
        },
        {
          field: 'driverEmailAddress',
          headerName: t('common.email') || '',
          flex: 1,
          sortable: false,
          renderCell: renderDriverEmail,
          cellClassName: FULLSTORY_MASK_CLASSNAME,
        },
        {
          field: 'preferredUserName',
          headerName: 'Username',
          flex: 1,
          sortable: false,
          renderCell: renderUsername,
        },
        {
          field: 'lastLoggedInOnUTC',
          headerName: t('driver.lastLogin') || '',
          flex: 1,
          renderCell: (params: GridCellParams): React.ReactElement => {
            return (
              <span>{useLastLoginRender(params.row.lastLoggedInOnUTC)}</span>
            );
          },
        },
      ],
      t,
      onDelete,
      onPasswordReset,
      true,
      'drivers.delete',
      onDriverEdit,
      'driverState',
    );
  }

  public getVehicleColumnDefinition(
    renderVehicleName: (params: GridCellParams) => React.ReactElement,
    renderVehicleType: (params: GridCellParams) => React.ReactElement,
    t: TFunction,
    onDelete:
      | ((
          evt: React.MouseEvent<HTMLButtonElement, MouseEvent>,
          params: GridCellParams,
        ) => void)
      | null,
    onEdit?: ((row: any) => void) | null,
  ) {
    return this.getColumnDefinition(
      [
        { field: 'vehicleID', headerName: 'ID', hide: true },
        {
          field: 'vehicleMake',
          headerName: t('vehicle.make') || '',
          flex: 1,
          renderCell: renderVehicleName,
        },
        {
          field: 'vehicleModel',
          headerName: t('vehicle.model') || '',
          flex: 1,
        },
        {
          field: 'vehicleYear',
          headerName: t('vehicle.year') || '',
          width: 100,
        },
        {
          field: 'vehicleTypeID',
          headerName: t('vehicle.type') || '',
          flex: 1,
          sortable: false,
          renderCell: renderVehicleType,
        },
        {
          field: 'licensePlateIssuedInStateCode',
          headerName: t('common.state') || '',
          width: 90,
        },
      ],
      t,
      onDelete,
      null,
      true,
      'vehicles.delete',
      onEdit || null,
      'vehicleState',
    );
  }

  public getUserColumnDefinition(
    renderUserLastName: (params: GridCellParams) => React.ReactElement,
    renderRoles: (params: GridCellParams) => React.ReactElement,
    t: TFunction,
    onDelete?: (id: number, email: string) => void,
    onPasswordReset?: (id: number, email: string) => void,
    currentUser?: any,
  ) {
    const colDef = this.getColumnDefinition(
      [
        { field: 'userAccountID', headerName: 'ID', hide: true },
        {
          field: 'userLastName',
          headerName: t('users.lastName') || '',
          flex: 1,
          sortable: true,
          cellClassName: FULLSTORY_MASK_CLASSNAME,
          renderCell: renderUserLastName,
        },
        {
          field: 'userFirstName',
          headerName: t('users.firstName') || '',
          flex: 1,
          sortable: true,
          cellClassName: FULLSTORY_MASK_CLASSNAME,
        },
        {
          field: 'emailAddress',
          headerName: t('users.email') || '',
          flex: 1,
          sortable: false,
          cellClassName: FULLSTORY_MASK_CLASSNAME,
        },
        {
          field: 'roles',
          headerName: t('users.roles') || '',
          width: 170,
          sortable: false,
          renderCell: renderRoles,
        },
      ],
      t,
      null,
      null,
      false,
      'common.delete',
      null,
      'userAccountState',
    );
    if (onDelete || onPasswordReset) {
      const deleteTitle = t('users.deleteUser');
      const resetTitle = t('users.resetPassword');
      colDef.push({
        field: 'UserActions',
        headerName: ' ',
        width: 120,
        sortable: false,
        renderCell: (params: GridCellParams) => (
          <>
            {onDelete && (
              <Tooltip title={deleteTitle}>
                <IconButton
                  data-testid="delete"
                  disabled={
                    currentUser.emailAddress === params.row.emailAddress
                  }
                  onClick={(evt) =>
                    onDelete(params.row.userAccountID, params.row.emailAddress)
                  }
                  color="primary"
                  size="large">
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )}
            {onPasswordReset && (
              <Tooltip title={resetTitle}>
                <IconButton
                  data-testid="password"
                  onClick={(evt) =>
                    onPasswordReset(
                      params.row.userAccountID,
                      params.row.emailAddress,
                    )
                  }
                  color="default"
                  size="large">
                  <PasswordIcon />
                </IconButton>
              </Tooltip>
            )}
          </>
        ),
      });
    }
    return colDef;
  }
}

const tableUtils = new TableUtils();

export default tableUtils;
